import type { Contract } from '@pm/schemas';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  ErrorState,
  Label,
  LabelCaps,
  PageHeader,
  Skeleton,
} from '@pm/ui';
import { Ban, ChevronRight, Pause, Play, Square } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { UserPicker } from '@/components/pickers';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtDate, fmtDateTime, fmtMoney } from '@/lib/format';
import { comKeys, useContract, useInvalidating } from '@/lib/queries';
import { useToast } from '@/lib/toast';
import { ContractBadge } from './commercial-bits';

/** SPEC §10 — contrato: serviços em snapshot, vigência, ativação que dispara workflows. */
export function ContractPage() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const { can } = useAuth();
  const { data: c, isLoading, isError, error, refetch } = useContract(id);
  const [activateOpen, setActivateOpen] = useState(false);
  const [confirm, setConfirm] = useState<'suspend' | 'end' | 'cancel' | null>(null);
  const inval = [comKeys.contract(id ?? ''), ['contracts'], ['tasks']];
  const transition = useInvalidating(
    (action: string) => api.post<Contract>(`/contracts/${id}/${action}`),
    inval,
  );
  if (isLoading)
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="w-40" />
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-40 rounded-md" />
      </div>
    );
  if (isError || !c) return <ErrorState message={error?.message} onRetry={() => void refetch()} />;
  const manage = can('contracts.manage');
  const labels = { suspend: 'Suspender', end: 'Encerrar', cancel: 'Cancelar' } as const;

  return (
    <div className="flex max-w-[1100px] flex-col gap-gap-lg">
      <PageHeader
        eyebrow={
          <div className="flex items-center gap-1.5 font-mono text-xs text-text-3">
            <Link to="/comercial?tab=contracts" className="hover:text-text-1">
              Contratos
            </Link>
            <ChevronRight className="size-3" />
            <span>#{c.number}</span>
          </div>
        }
        title={`Contrato #${c.number} · ${c.client.name}`}
        actions={
          <>
            <ContractBadge status={c.status} />
            {manage && (c.status === 'DRAFT' || c.status === 'SUSPENDED') ? (
              <Button variant="primary" onClick={() => setActivateOpen(true)}>
                <Play /> Ativar
              </Button>
            ) : null}
            {manage && c.status === 'ACTIVE' ? (
              <Button variant="secondary" onClick={() => setConfirm('suspend')}>
                <Pause /> Suspender
              </Button>
            ) : null}
            {manage && (c.status === 'ACTIVE' || c.status === 'SUSPENDED') ? (
              <Button variant="ghost" onClick={() => setConfirm('end')}>
                <Square /> Encerrar
              </Button>
            ) : null}
            {manage && c.status !== 'ENDED' && c.status !== 'CANCELED' ? (
              <Button variant="ghost" className="text-danger" onClick={() => setConfirm('cancel')}>
                <Ban /> Cancelar
              </Button>
            ) : null}
          </>
        }
      />
      <div className="grid gap-gap-lg lg:grid-cols-[1fr_300px]">
        <section className="rounded-md border border-border bg-surface-1">
          <header className="border-b border-border px-4 py-2.5">
            <LabelCaps>Serviços contratados</LabelCaps>
          </header>
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-bg font-mono text-xs uppercase tracking-[var(--tracking-caps)] text-text-3">
                <th className="h-8 px-3 text-left font-medium">Serviço</th>
                <th className="h-8 px-3 text-right font-medium">Qtd</th>
                <th className="h-8 px-3 text-right font-medium">Valor</th>
                <th className="h-8 px-3 text-left font-medium">Demanda</th>
              </tr>
            </thead>
            <tbody>
              {c.services.map((s) => (
                <tr key={s.id} className="h-row border-b border-border last:border-b-0">
                  <td className="px-3 text-sm">
                    {s.service_name}
                    {!s.service ? (
                      <span className="ml-2 font-mono text-[10px] text-text-3">avulso</span>
                    ) : null}
                  </td>
                  <td className="px-3 text-right font-mono text-sm">{s.quantity}</td>
                  <td className="px-3 text-right font-mono text-sm">{fmtMoney(s.price)}</td>
                  <td className="px-3 text-sm">
                    {s.task ? (
                      <Link to={`/tasks/${s.task.id}`} className="hover:text-accent">
                        <span className="font-mono text-xs text-text-3">#{s.task.number}</span>{' '}
                        {s.task.title}
                      </Link>
                    ) : (
                      <span className="text-text-3">
                        {c.status === 'DRAFT' ? 'gerada na ativação' : '—'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {c.notes ? (
            <p className="border-t border-border px-4 py-3 text-sm whitespace-pre-wrap text-text-2">
              {c.notes}
            </p>
          ) : null}
        </section>
        <aside className="flex flex-col gap-4 self-start rounded-md border border-border bg-surface-1 p-4">
          <div>
            <LabelCaps>Total</LabelCaps>
            <p className="font-mono text-2xl">{fmtMoney(c.total)}</p>
          </div>
          <div>
            <LabelCaps>Vigência</LabelCaps>
            <p className="font-mono text-sm">
              {fmtDate(`${c.start_date}T12:00:00`)} →{' '}
              {c.end_date ? fmtDate(`${c.end_date}T12:00:00`) : 'indeterminado'}
            </p>
          </div>
          <div>
            <LabelCaps>Cliente</LabelCaps>
            <Link to={`/clientes/${c.client.id}`} className="text-sm hover:text-accent">
              {c.client.name}
            </Link>
          </div>
          {c.proposal ? (
            <div>
              <LabelCaps>Origem</LabelCaps>
              <Link
                to={`/comercial/propostas/${c.proposal.id}`}
                className="text-sm hover:text-accent"
              >
                Proposta #{c.proposal.number}
              </Link>
            </div>
          ) : null}
          <div className="flex flex-col gap-1 font-mono text-xs text-text-3">
            <span>
              Criado {fmtDateTime(c.created_at)}
              {c.created_by ? ` · ${c.created_by.name}` : ''}
            </span>
            {c.activated_at ? (
              <span>
                Ativado {fmtDateTime(c.activated_at)}
                {c.activated_by ? ` · ${c.activated_by.name}` : ''}
              </span>
            ) : null}
          </div>
        </aside>
      </div>
      <Dialog open={activateOpen} onOpenChange={setActivateOpen}>
        <DialogContent
          title="Ativar contrato"
          description={
            c.activated_at
              ? 'Reativação: os workflows já iniciados não são duplicados.'
              : 'Cada serviço com workflow vinculado vira uma demanda no contexto do cliente. Automações com gatilho "Contrato ativado" também rodam.'
          }
          size="sm"
        >
          <ActivateForm
            contractId={c.id}
            first={!c.activated_at}
            onDone={() => setActivateOpen(false)}
          />
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={confirm !== null}
        onOpenChange={() => setConfirm(null)}
        title={`${confirm ? labels[confirm] : ''} contrato #${c.number}?`}
        description={
          confirm === 'cancel'
            ? 'Não pode ser desfeito.'
            : confirm === 'end'
              ? 'Define a data de fim como hoje se não houver.'
              : undefined
        }
        confirmLabel={confirm ? labels[confirm] : ''}
        danger={confirm === 'cancel'}
        loading={transition.isPending}
        onConfirm={() =>
          confirm &&
          transition.mutate(confirm, {
            onSuccess: () => (toast.success('Contrato atualizado'), setConfirm(null)),
            onError: (e) => toast.error(e),
          })
        }
      />
    </div>
  );
}

function ActivateForm({
  contractId,
  first,
  onDone,
}: {
  contractId: string;
  first: boolean;
  onDone: () => void;
}) {
  const toast = useToast();
  const [startWorkflows, setStartWorkflows] = useState(true);
  const [responsible, setResponsible] = useState<string | null>(null);
  const activate = useInvalidating(
    (input: Record<string, unknown>) =>
      api.post<Contract>(`/contracts/${contractId}/activate`, input),
    [comKeys.contract(contractId), ['contracts'], ['tasks']],
  );
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        activate.mutate(
          { start_workflows: startWorkflows, responsible_user_id: responsible },
          {
            onSuccess: () => (toast.success('Contrato ativado'), onDone()),
            onError: (err) => toast.error(err),
          },
        );
      }}
    >
      {first ? (
        <>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={startWorkflows}
              onCheckedChange={(v) => setStartWorkflows(v === true)}
            />{' '}
            Iniciar workflows dos serviços
          </label>
          <div className="flex flex-col gap-1.5">
            <Label>Responsável pelas demandas</Label>
            <UserPicker value={responsible} onChange={setResponsible} />
          </div>
        </>
      ) : null}
      <DialogFooter>
        <Button variant="ghost" type="button" onClick={onDone}>
          Cancelar
        </Button>
        <Button variant="primary" type="submit" loading={activate.isPending}>
          <Play /> Ativar
        </Button>
      </DialogFooter>
    </form>
  );
}
